import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { Factor } from "../data/schema";
import { useFactors } from "../state/FactorsContext";
import FactorSlider from "./FactorSlider";
import ConfidenceBadge from "./ConfidenceBadge";

/**
 * One factor's card: title, tooltip, confidence badge, the slider, and an
 * expandable deeper explanation. Fully driven by a `Factor` object — adding a
 * factor to factors.json renders a new card with zero code changes.
 */
export default function FactorCard({ factor }: { factor: Factor }) {
  const { positions, setPosition } = useFactors();
  const [open, setOpen] = useState(false);
  const position = positions[factor.id] ?? 0.5;

  return (
    <div className="rounded-2xl border border-white/[0.07] bg-nebula/40 p-5 backdrop-blur-sm transition-colors hover:border-white/15">
      <div className="mb-1 flex items-start justify-between gap-3">
        <h3 className="font-display text-lg font-normal text-white">
          {factor.title}
        </h3>
        <ConfidenceBadge confidence={factor.confidence} />
      </div>

      {/* Tooltip line — always visible, sets the one-sentence context. */}
      <p className="mb-4 text-sm leading-relaxed text-white/75">
        {factor.tooltip}
      </p>

      <FactorSlider
        factor={factor}
        position={position}
        onChange={(t) => setPosition(factor.id, t)}
      />

      {/* Deeper explanation, collapsed by default. */}
      {factor.explanation && (
        <div className="mt-4 border-t border-white/[0.06] pt-3">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="flex items-center gap-1.5 text-xs font-medium text-cyan/70 transition-colors hover:text-cyan"
          >
            {open ? "Hide" : "Why this matters"}
            <motion.span
              animate={{ rotate: open ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              aria-hidden
            >
              ▾
            </motion.span>
          </button>
          <AnimatePresence initial={false}>
            {open && (
              <motion.p
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden text-sm leading-relaxed text-white/80"
              >
                <span className="block pt-3">{factor.explanation}</span>
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
